import React, { useRef, useEffect, useState } from "react";
import { Button, message, Modal, Input, Form, Radio, Dropdown } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LinkOutlined,
  PictureOutlined,
  AppstoreOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  UndoOutlined,
  RedoOutlined,
  FontSizeOutlined,
  BorderHorizontalOutlined,
  DownOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { validateImageFile, uploadCampaignFileToS3 } from "../../../utils/campaignsS3Upload";
import {
  enrichHtmlForEditor,
  EMAIL_CTA_BUTTON_STYLE,
  buildCtaRowStyle,
} from "../../../utils/campaignEmailContent";
import {
  attachAllMediaWrappers,
  setupMediaWrapper,
  teardownMediaInteractions,
} from "./mediaWrapperInteractions";
import "./RichTextEditor.css";

const BLOCK_FORMAT_OPTIONS = [
  { key: "p", label: "Paragraph" },
  { key: "h1", label: "Heading 1" },
  { key: "h2", label: "Heading 2" },
  { key: "h3", label: "Heading 3" },
  { key: "h4", label: "Heading 4" },
  { key: "h5", label: "Heading 5" },
  { key: "h6", label: "Heading 6" },
];

const FONT_SIZE_OPTIONS = [12, 14, 16, 18, 20, 24, 28, 32];
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 72;
const DEFAULT_FONT_SIZE = 16;

const RichTextEditor = ({ value, onChange, placeholder, dir }) => {
  const editorRef = useRef(null);
  const wrapperRef = useRef(null);
  const mediaInputRef = useRef(null);
  const pendingMediaTypeRef = useRef("image");
  const mediaUploadQueueRef = useRef(Promise.resolve());
  const mediaUploadJobsRef = useRef({ image: 0, icon: 0 });
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrike, setIsStrike] = useState(false);
  const [currentFontSize, setCurrentFontSize] = useState(DEFAULT_FONT_SIZE);
  const [uploading, setUploading] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [buttonForm] = Form.useForm();
  const [linkForm] = Form.useForm();
  const buttonRangeRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [editingButton, setEditingButton] = useState(null);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [hoveredButtonWrapper, setHoveredButtonWrapper] = useState(null);
  const [buttonToolbarPosition, setButtonToolbarPosition] = useState(null);

  useEffect(() => {
    if (editorRef.current) {
      const newValue = enrichHtmlForEditor(value || "");
      const currentHtml = editorRef.current.innerHTML;

      // Only update if different to avoid cursor jumping
      if (currentHtml !== newValue && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = newValue;
      }
      attachAllMediaWrappers(editorRef.current, handleChange);
    }
  }, [value]);

  useEffect(() => {
    return () => teardownMediaInteractions();
  }, []);

  const handleEditorMouseMove = (e) => {
    if (!editorRef.current || !(e.target instanceof Node) || !editorRef.current.contains(e.target)) return;
    if (!wrapperRef.current) return;
    const wrapper = e.target.closest?.(".campaign-cta-button-wrapper");
    const anchor = e.target.closest?.("a.campaign-cta-button");
    const el = wrapper || (anchor && !anchor.closest(".campaign-cta-button-wrapper") ? anchor : null);
    if (el) {
      const buttonEl = el.classList?.contains("campaign-cta-button-wrapper")
        ? el.querySelector("a.campaign-cta-button") || el
        : el;
      const rect = buttonEl.getBoundingClientRect();
      const wrapRect = wrapperRef.current.getBoundingClientRect();
      setHoveredButtonWrapper(el);
      setButtonToolbarPosition({
        top: rect.bottom - wrapRect.top + 6,
        left: rect.left - wrapRect.left + rect.width / 2,
      });
    } else {
      setHoveredButtonWrapper(null);
      setButtonToolbarPosition(null);
    }
  };

  const handleEditorMouseLeave = (e) => {
    const related = e.relatedTarget;
    if (related instanceof Node) {
      if (related.closest?.(".campaign-cta-button-alignment-toolbar")) return;
      if (editorRef.current?.contains(related)) return;
    }
    setHoveredButtonWrapper(null);
    setButtonToolbarPosition(null);
  };

  const handleEditorScroll = () => {
    if (hoveredButtonWrapper && wrapperRef.current) {
      const buttonEl = hoveredButtonWrapper.classList?.contains("campaign-cta-button-wrapper")
        ? hoveredButtonWrapper.querySelector("a.campaign-cta-button") || hoveredButtonWrapper
        : hoveredButtonWrapper;
      const rect = buttonEl.getBoundingClientRect();
      const wrapRect = wrapperRef.current.getBoundingClientRect();
      setButtonToolbarPosition({
        top: rect.bottom - wrapRect.top + 6,
        left: rect.left - wrapRect.left + rect.width / 2,
      });
    }
  };

  const applyButtonAlignment = (align) => {
    if (!hoveredButtonWrapper) return;
    const wrapper = hoveredButtonWrapper.classList?.contains("campaign-cta-button-wrapper")
      ? hoveredButtonWrapper
      : (() => {
          const anchor = hoveredButtonWrapper;
          const wrap = document.createElement("div");
          wrap.className = "campaign-cta-button-wrapper campaign-cta-align-" + align;
          wrap.setAttribute("contenteditable", "false");
          wrap.setAttribute("data-campaign-cta-align", align);
          wrap.setAttribute("style", buildCtaRowStyle(align));
          anchor.parentNode.insertBefore(wrap, anchor);
          wrap.appendChild(anchor);
          return wrap;
        })();
    if (wrapper.classList.contains("campaign-cta-button-wrapper")) {
      wrapper.className = "campaign-cta-button-wrapper campaign-cta-align-" + align;
      wrapper.setAttribute("data-campaign-cta-align", align);
      wrapper.setAttribute("style", buildCtaRowStyle(align));
    }
    const anchor = wrapper.querySelector?.("a.campaign-cta-button") || hoveredButtonWrapper;
    if (anchor?.tagName === "A") {
      anchor.setAttribute("data-campaign-cta-align", align);
    }
    handleChange();
  };

  const saveEditorSelection = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const restoreEditorSelection = () => {
    if (!savedSelectionRef.current || !editorRef.current) return false;
    const selection = window.getSelection();
    if (!selection) return false;
    editorRef.current.focus();
    selection.removeAllRanges();
    selection.addRange(savedSelectionRef.current);
    return true;
  };

  const handleEditorSelectionChange = () => {
    saveEditorSelection();
    updateToolbarState();
  };

  const isNonTextContext = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current) return false;
    let node = selection.getRangeAt(0).commonAncestorContainer;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
    if (!(node instanceof Element)) return false;
    return Boolean(
      node.closest(
        ".editor-image-wrapper, .editor-icon-wrapper, .campaign-cta-button-wrapper, .campaign-cta-button",
      ),
    );
  };

  const execCommand = (command, value = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, value);
    updateToolbarState();
    handleChange();
  };

  const execTextCommand = (command, value = null) => {
    if (isNonTextContext()) return;
    execCommand(command, value);
  };

  const applyBlockFormat = (tag) => {
    if (editorRef.current) editorRef.current.focus();

    const selection = window.getSelection();
    const hasValidSelection =
      selection?.rangeCount &&
      editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer);

    if (!hasValidSelection) {
      if (!restoreEditorSelection()) return;
    }

    if (isNonTextContext()) return;

    // Browsers require angle brackets, e.g. "<h2>" not "h2"
    document.execCommand("formatBlock", false, `<${tag}>`);
    saveEditorSelection();
    updateToolbarState();
    handleChange();
  };

  // Wraps the selection in inline-styled spans (email clients ignore classes)
  const applyFontSize = (px) => {
    if (editorRef.current) editorRef.current.focus();

    const selection = window.getSelection();
    const hasValidSelection =
      selection?.rangeCount &&
      editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer);
    if (!hasValidSelection && !restoreEditorSelection()) return;
    if (isNonTextContext()) return;

    // execCommand("fontSize") only supports 1-7, so mark with 7 then swap for real px spans
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, "7");
    editorRef.current.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);
      // drop nested size spans so the new size wins
      span.querySelectorAll("span").forEach((inner) => {
        if (inner.style.fontSize) {
          inner.style.fontSize = "";
          if (!inner.getAttribute("style")) inner.replaceWith(...inner.childNodes);
        }
      });
    });

    saveEditorSelection();
    updateToolbarState();
    handleChange();
  };

  const stepFontSize = (delta) => {
    const next = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, currentFontSize + delta));
    if (next === currentFontSize) return;
    applyFontSize(next);
    setCurrentFontSize(next);
  };

  const openLinkModal = () => {
    if (isNonTextContext()) return;
    if (editorRef.current) editorRef.current.focus();

    let existingUrl = "";
    const selection = window.getSelection();
    if (selection?.rangeCount) {
      let node = selection.getRangeAt(0).commonAncestorContainer;
      if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
      const anchor = node instanceof Element ? node.closest("a") : null;
      if (anchor && editorRef.current?.contains(anchor)) {
        existingUrl = anchor.getAttribute("href") || anchor.href || "";
      }
    }

    linkForm.setFieldsValue({ url: existingUrl });
    setShowLinkModal(true);
  };

  const handleLinkModalOk = () => {
    linkForm.validateFields().then(({ url }) => {
      execTextCommand("createLink", url);
      setShowLinkModal(false);
      linkForm.resetFields();
    });
  };

  const handleLinkModalCancel = () => {
    setShowLinkModal(false);
    linkForm.resetFields();
  };

  const updateToolbarState = () => {
    if (editorRef.current) {
      setIsBold(document.queryCommandState("bold"));
      setIsItalic(document.queryCommandState("italic"));
      setIsUnderline(document.queryCommandState("underline"));
      setIsStrike(document.queryCommandState("strikeThrough"));
      const sel = window.getSelection();
      if (sel?.rangeCount) {
        let node = sel.getRangeAt(0).startContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
        if (node instanceof Element && editorRef.current.contains(node)) {
          const px = Math.round(parseFloat(window.getComputedStyle(node).fontSize));
          if (px) setCurrentFontSize(px);
        }
      }
    }
  };

  const insertMediaAtSelection = (imageUrl, mediaType = "image") => {
    const isIcon = mediaType === "icon";
    const wrapperClass = isIcon ? "editor-icon-wrapper" : "editor-image-wrapper";
    const imgClass = isIcon ? "editor-icon" : "editor-image";

    if (editorRef.current) {
      editorRef.current.focus();
    }

    const selection = window.getSelection();

    const wrapper = document.createElement("div");
    wrapper.className = wrapperClass;
    wrapper.contentEditable = "false";
    wrapper.setAttribute("contenteditable", "false");
    wrapper.style.cssText = isIcon
      ? "position: relative; display: inline-block; margin: 4px 8px 4px 0; vertical-align: middle; user-select: none;"
      : "position: relative; display: inline-block; margin: 8px 0; max-width: 100%; user-select: none;";

    const img = document.createElement("img");
    img.src = imageUrl;
    img.className = imgClass;
    img.contentEditable = "false";
    img.alt = isIcon ? "Icon" : "Image";
    img.style.cssText = isIcon
      ? "width: 48px; height: 48px; max-width: 48px; max-height: 48px; object-fit: contain; display: block; border-radius: 4px; pointer-events: none;"
      : "max-width: 100%; height: auto; display: block; border-radius: 4px; pointer-events: none;";
    if (isIcon) {
      img.setAttribute("data-campaign-media", "icon");
    } else {
      img.setAttribute("data-campaign-media", "image");
    }

    const removeBtn = document.createElement("button");
    removeBtn.className = isIcon ? "editor-icon-remove-btn" : "editor-image-remove-btn";
    removeBtn.innerHTML = "×";
    removeBtn.contentEditable = "false";
    removeBtn.setAttribute("contenteditable", "false");
    removeBtn.style.cssText = `
      position: absolute;
      top: ${isIcon ? "2px" : "8px"};
      right: ${isIcon ? "2px" : "8px"};
      background: rgba(0, 0, 0, 0.7);
      color: white;
      border: none;
      border-radius: 50%;
      width: ${isIcon ? "22px" : "28px"};
      height: ${isIcon ? "22px" : "28px"};
      cursor: pointer !important;
      font-size: ${isIcon ? "14px" : "18px"};
      line-height: 1;
      padding: 0;
      margin: 0;
      display: flex;
      opacity: 0;
      visibility: hidden;
      align-items: center;
      justify-content: center;
      font-family: Arial, sans-serif;
      user-select: none;
      pointer-events: auto;
      z-index: 10;
      transition: all 0.2s;
    `;
    removeBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      wrapper.remove();
      handleChange();
    };
    removeBtn.onmousedown = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    wrapper.onmouseenter = () => {
      removeBtn.style.opacity = "1";
      removeBtn.style.visibility = "visible";
    };
    wrapper.onmouseleave = () => {
      removeBtn.style.opacity = "0";
      removeBtn.style.visibility = "hidden";
    };
    wrapper.onkeydown = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    wrapper.appendChild(img);
    wrapper.appendChild(removeBtn);

    if (editorRef.current) {
      setupMediaWrapper(wrapper, {
        editorEl: editorRef.current,
        onUpdate: handleChange,
      });
    }

    let shouldInsertAtCursor = false;
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      if (editorRef.current && editorRef.current.contains(container)) {
        shouldInsertAtCursor = true;
      }
    }

    if (shouldInsertAtCursor) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(wrapper);
      const spacer = document.createTextNode("\u00A0");
      range.setStartAfter(wrapper);
      range.insertNode(spacer);
      range.setStartAfter(spacer);
      selection.removeAllRanges();
      selection.addRange(range);
    } else if (editorRef.current) {
      editorRef.current.appendChild(wrapper);
      const spacer = document.createTextNode("\u00A0");
      editorRef.current.appendChild(spacer);
      editorRef.current.focus();
      const range = document.createRange();
      range.setStartAfter(spacer);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    handleChange();
  };

  const handleChange = () => {
    if (editorRef.current && onChange) {
      const html = editorRef.current.innerHTML;
      // Normalize empty content
      const normalizedHtml = html === "<br>" || html === "<div><br></div>" ? "" : html;
      onChange(normalizedHtml);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    // Keep paste as plain text so HTML source stays visible as code in Content.
    // Designed layout is rendered only on the Preview step (and when sending).
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    handleChange();
  };

  const insertButton = () => {
    // Check if cursor is inside a button and inside THIS editor
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      
      // Check if selection is within this editor
      if (editorRef.current && editorRef.current.contains(container)) {
        let node = container;
        
        // Traverse up to check if we're inside a button
        while (node && node !== editorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if ((node.tagName === "A" && node.classList.contains("campaign-cta-button")) ||
                node.classList?.contains("campaign-cta-button-wrapper")) {
              message.error("Cannot insert a button inside another button");
              return;
            }
          }
          node = node.parentNode;
        }
        
        buttonRangeRef.current = range;
      } else {
        // Selection is not in this editor, will insert at end
        buttonRangeRef.current = null;
      }
    } else {
      buttonRangeRef.current = null;
    }
    
    // Reset form and show modal
    setEditingButton(null);
    buttonForm.resetFields();
    setShowButtonModal(true);
  };

  const handleButtonModalOk = () => {
    buttonForm.validateFields().then((values) => {
      const { buttonText, buttonLink, buttonAlignment = "left" } = values;
      
      // If editing existing button, update it
      if (editingButton) {
        editingButton.textContent = buttonText;
        editingButton.href = buttonLink;
        editingButton.classList.add("preview-button", "campaign-cta-button", "campaign-editor-button");
        editingButton.setAttribute("data-campaign-cta", "true");
        editingButton.setAttribute("contenteditable", "false");
        editingButton.setAttribute("style", EMAIL_CTA_BUTTON_STYLE);
        editingButton.setAttribute("data-campaign-cta-style", EMAIL_CTA_BUTTON_STYLE);
        editingButton.setAttribute("data-campaign-cta-align", buttonAlignment);
        const wrapper = editingButton.closest?.(".campaign-cta-button-wrapper");
        if (wrapper) {
          wrapper.className = "campaign-cta-button-wrapper campaign-cta-align-" + buttonAlignment;
          wrapper.setAttribute("data-campaign-cta-align", buttonAlignment);
          wrapper.setAttribute("style", buildCtaRowStyle(buttonAlignment));
        } else {
          const wrap = document.createElement("div");
          wrap.className = "campaign-cta-button-wrapper campaign-cta-align-" + buttonAlignment;
          wrap.setAttribute("contenteditable", "false");
          wrap.setAttribute("data-campaign-cta-align", buttonAlignment);
          wrap.setAttribute("style", buildCtaRowStyle(buttonAlignment));
          editingButton.parentNode.insertBefore(wrap, editingButton);
          wrap.appendChild(editingButton);
        }
        handleChange();
        setShowButtonModal(false);
        setEditingButton(null);
        buttonForm.resetFields();
        return;
      }
      
      const anchorHtml = `<a href="${buttonLink}" class="preview-button campaign-cta-button campaign-editor-button" data-campaign-cta="true" data-campaign-cta-align="${buttonAlignment}" data-campaign-cta-style="${EMAIL_CTA_BUTTON_STYLE}" style="${EMAIL_CTA_BUTTON_STYLE}" contenteditable="false">${buttonText}</a>`;
      const wrapperHtml = `<div class="campaign-cta-button-wrapper campaign-cta-align-${buttonAlignment}" data-campaign-cta-align="${buttonAlignment}" contenteditable="false" style="${buildCtaRowStyle(buttonAlignment)}">${anchorHtml}</div>`;
      
      const range = buttonRangeRef.current;
      const shouldInsertAtCursor = range &&
        editorRef.current &&
        editorRef.current.contains(range.commonAncestorContainer);
      
      if (shouldInsertAtCursor) {
        const selection = window.getSelection();
        range.deleteContents();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = wrapperHtml;
        const wrapperNode = tempDiv.firstChild;
        range.insertNode(wrapperNode);
        const space = document.createTextNode("\u00A0");
        range.setStartAfter(wrapperNode);
        range.insertNode(space);
        range.setStartAfter(space);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        if (editorRef.current) {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = wrapperHtml;
          editorRef.current.appendChild(tempDiv.firstChild);
          const space = document.createTextNode("\u00A0");
          editorRef.current.appendChild(space);
          editorRef.current.focus();
          const selection = window.getSelection();
          const newRange = document.createRange();
          newRange.setStartAfter(space);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
      
      handleChange();
      setShowButtonModal(false);
      buttonForm.resetFields();
    }).catch((errorInfo) => {
      console.log("Validation failed:", errorInfo);
    });
  };

  const handleButtonModalCancel = () => {
    setShowButtonModal(false);
    setEditingButton(null);
    buttonForm.resetFields();
  };

  const handleEditorClick = (e) => {
    const target = e.target;
    if (target.tagName === "A" && target.classList.contains("campaign-cta-button")) {
      e.preventDefault();
      const wrapper = target.closest(".campaign-cta-button-wrapper");
      const alignment = wrapper
        ? (wrapper.classList.contains("campaign-cta-align-center")
            ? "center"
            : wrapper.classList.contains("campaign-cta-align-right")
            ? "right"
            : "left")
        : "left";
      setEditingButton(target);
      buttonForm.setFieldsValue({
        buttonText: target.textContent,
        buttonLink: target.href,
        buttonAlignment: alignment,
      });
      setShowButtonModal(true);
    }
  };

  const removeButton = () => {
    if (editingButton) {
      editingButton.remove();
      handleChange();
      setShowButtonModal(false);
      setEditingButton(null);
      buttonForm.resetFields();
    }
  };

  const removeImage = (img) => {
    if (img && img.parentNode) {
      img.remove();
      handleChange();
      setHoveredImage(null);
    }
  };

  const processMediaFile = async (file, mediaType = "image") => {
    const isIcon = mediaType === "icon";
    validateImageFile(file, isIcon ? 2 : 10);
    const { url: imageUrl } = await uploadCampaignFileToS3(file);
    insertMediaAtSelection(imageUrl, mediaType);
  };

  const updateMediaUploadLoading = (mediaType) => {
    const active = mediaUploadJobsRef.current[mediaType] > 0;
    if (mediaType === "icon") setUploadingIcon(active);
    else setUploading(active);
  };

  const openMediaPicker = (mediaType) => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    pendingMediaTypeRef.current = mediaType;
    mediaInputRef.current?.click();
  };

  const enqueueMediaUploads = (fileList, mediaType = "image") => {
    const files = Array.from(fileList || []).filter((file) => file instanceof File);
    if (!files.length) return;

    const isIcon = mediaType === "icon";
    const label = isIcon ? "icons" : "images";

    mediaUploadJobsRef.current[mediaType] += 1;
    updateMediaUploadLoading(mediaType);

    mediaUploadQueueRef.current = mediaUploadQueueRef.current
      .then(async () => {
        let successCount = 0;
        for (const file of files) {
          try {
            await processMediaFile(file, mediaType);
            successCount += 1;
          } catch (error) {
            console.error("Upload error:", error);
            message.error(
              error.message ||
                (isIcon ? "Failed to upload icons" : "Failed to upload images"),
            );
          }
        }
        if (successCount > 0) {
          message.success(
            `${successCount} ${label} uploaded successfully`,
          );
        }
      })
      .finally(() => {
        mediaUploadJobsRef.current[mediaType] = Math.max(
          0,
          mediaUploadJobsRef.current[mediaType] - 1,
        );
        updateMediaUploadLoading(mediaType);
      });
  };

  const handleMediaFileInputChange = (event) => {
    const { files } = event.target;
    enqueueMediaUploads(files, pendingMediaTypeRef.current);
    event.target.value = "";
  };

  return (
    <div ref={wrapperRef} className="rich-text-editor-wrapper rich-text-editor-wrapper-with-toolbar">
      <input
        ref={mediaInputRef}
        type="file"
        accept="image/*,.svg,.png,.webp,.gif"
        multiple
        tabIndex={-1}
        aria-hidden="true"
        className="rich-text-editor-file-input"
        onChange={handleMediaFileInputChange}
      />
      <div className="rich-text-editor-toolbar">
        <div className="rich-text-editor-toolbar-section rich-text-editor-toolbar-section-text">
        <Button
          type={isBold ? "primary" : "default"}
          icon={<BoldOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("bold");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Bold"
        />
        <Button
          type={isItalic ? "primary" : "default"}
          icon={<ItalicOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("italic");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Italic"
        />
        <Button
          type={isUnderline ? "primary" : "default"}
          icon={<UnderlineOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("underline");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Underline"
        />
        <Button
          type={isStrike ? "primary" : "default"}
          icon={<StrikethroughOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("strikeThrough");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Strikethrough"
        />
        <span className="rich-text-editor-toolbar-divider" />
        <Dropdown
          onOpenChange={(open) => {
            if (open) saveEditorSelection();
          }}
          menu={{
            items: BLOCK_FORMAT_OPTIONS.map((opt) => ({
              key: opt.key,
              label: (
                <span
                  className={`rich-text-editor-block-option rich-text-editor-block-option-${opt.key}`}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {opt.label}
                </span>
              ),
              onClick: () => applyBlockFormat(opt.key),
            })),
          }}
          trigger={["click"]}
        >
          <Button
            type="default"
            icon={<FontSizeOutlined />}
            className="rich-text-editor-toolbar-btn rich-text-editor-toolbar-btn-dropdown"
            title="Heading / Paragraph"
            onMouseDown={(e) => {
              e.preventDefault();
              saveEditorSelection();
            }}
          >
            <DownOutlined className="rich-text-editor-toolbar-caret" />
          </Button>
        </Dropdown>
        <div className="rich-text-editor-size-group">
        <Button
          type="default"
          icon={<MinusOutlined />}
          onMouseDown={(e) => {
            e.preventDefault();
            saveEditorSelection();
          }}
          onClick={() => stepFontSize(-1)}
          disabled={currentFontSize <= MIN_FONT_SIZE}
          className="rich-text-editor-toolbar-btn"
          title="Decrease font size"
        />
        <Dropdown
          onOpenChange={(open) => {
            if (open) saveEditorSelection();
          }}
          menu={{
            items: FONT_SIZE_OPTIONS.map((px) => ({
              key: String(px),
              label: <span onMouseDown={(e) => e.preventDefault()}>{px}px</span>,
              onClick: () => {
                applyFontSize(px);
                setCurrentFontSize(px);
              },
            })),
          }}
          trigger={["click"]}
        >
          <Button
            type="default"
            className="rich-text-editor-toolbar-btn rich-text-editor-toolbar-btn-dropdown"
            title="Font size (px)"
            onMouseDown={(e) => {
              e.preventDefault();
              saveEditorSelection();
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, minWidth: 18 }}>{currentFontSize}</span>
            <DownOutlined className="rich-text-editor-toolbar-caret" />
          </Button>
        </Dropdown>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onMouseDown={(e) => {
            e.preventDefault();
            saveEditorSelection();
          }}
          onClick={() => stepFontSize(1)}
          disabled={currentFontSize >= MAX_FONT_SIZE}
          className="rich-text-editor-toolbar-btn"
          title="Increase font size"
        />
        </div>
        <Button
          type="default"
          icon={<AlignLeftOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("justifyLeft");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Align Left"
        />
        <Button
          type="default"
          icon={<AlignCenterOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("justifyCenter");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Align Center"
        />
        <Button
          type="default"
          icon={<AlignRightOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("justifyRight");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Align Right"
        />
        <span className="rich-text-editor-toolbar-divider" />
        <Button
          type="default"
          icon={<UnorderedListOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("insertUnorderedList");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Bullet List"
        />
        <Button
          type="default"
          icon={<OrderedListOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("insertOrderedList");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Numbered List"
        />
        <Button
          type="default"
          icon={<LinkOutlined />}
          onClick={openLinkModal}
          className="rich-text-editor-toolbar-btn"
          title="Insert Link"
        />
        <Button
          type="default"
          icon={<BorderHorizontalOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("insertHorizontalRule");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Horizontal Line"
        />
        <span className="rich-text-editor-toolbar-divider" />
        <Button
          type="default"
          icon={<UndoOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("undo");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Undo"
        />
        <Button
          type="default"
          icon={<RedoOutlined />}
          onClick={() => {
            if (editorRef.current) editorRef.current.focus();
            execTextCommand("redo");
          }}
          className="rich-text-editor-toolbar-btn"
          title="Redo"
        />
        </div>

        <span className="rich-text-editor-toolbar-section-divider" aria-hidden="true" />

        <div className="rich-text-editor-toolbar-section rich-text-editor-toolbar-section-media">
        <Button
          type="default"
          icon={<PictureOutlined />}
          loading={uploading}
          className="rich-text-editor-toolbar-btn rich-text-editor-toolbar-btn-media"
          title="Insert Images"
          onClick={() => openMediaPicker("image")}
        />
        <Button
          type="default"
          icon={<AppstoreOutlined />}
          loading={uploadingIcon}
          className="rich-text-editor-toolbar-btn rich-text-editor-toolbar-btn-media"
          title="Insert Icons"
          onClick={() => openMediaPicker("icon")}
        />
        <Button
          type="default"
          onClick={() => {
            if (editorRef.current) {
              editorRef.current.focus();
            }
            insertButton();
          }}
          className="rich-text-editor-toolbar-btn rich-text-editor-toolbar-btn-media"
          title="Insert Button"
        >
          Insert Button
        </Button>
        </div>
      </div>
      <div
        ref={editorRef}
        className="rich-text-editor-content"
        contentEditable
        dir={dir}
        onInput={handleChange}
        onPaste={handlePaste}
        onScroll={handleEditorScroll}
        onMouseMove={handleEditorMouseMove}
        onMouseLeave={handleEditorMouseLeave}
        onMouseUp={handleEditorSelectionChange}
        onKeyUp={handleEditorSelectionChange}
        onClick={handleEditorClick}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {/* Button alignment toolbar on hover - inside wrapper so it moves with the button */}
      {hoveredButtonWrapper && buttonToolbarPosition && (
        <div
          className="campaign-cta-button-alignment-toolbar"
          style={{
            position: "absolute",
            top: buttonToolbarPosition.top,
            left: buttonToolbarPosition.left,
            transform: "translateX(-50%)",
            zIndex: 10,
          }}
          onMouseLeave={() => {
            setHoveredButtonWrapper(null);
            setButtonToolbarPosition(null);
          }}
        >
          <Button
            type="default"
            size="small"
            icon={<AlignLeftOutlined />}
            title="Align left"
            onClick={() => applyButtonAlignment("left")}
            className="alignment-toolbar-btn"
          />
          <Button
            type="default"
            size="small"
            icon={<AlignCenterOutlined />}
            title="Align center"
            onClick={() => applyButtonAlignment("center")}
            className="alignment-toolbar-btn"
          />
          <Button
            type="default"
            size="small"
            icon={<AlignRightOutlined />}
            title="Align right"
            onClick={() => applyButtonAlignment("right")}
            className="alignment-toolbar-btn"
          />
        </div>
      )}

      <Modal
        title="Insert Link"
        open={showLinkModal}
        onOk={handleLinkModalOk}
        onCancel={handleLinkModalCancel}
        okText="Insert"
        cancelText="Cancel"
        width={480}
        destroyOnClose
      >
        <Form form={linkForm} layout="vertical">
          <Form.Item
            label="URL"
            name="url"
            rules={[
              { required: true, message: "Please enter a URL" },
              { type: "url", message: "Please enter a valid URL (e.g. https://example.com)" },
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Button Insert/Edit Modal */}
      <Modal
        title={editingButton ? "Edit Button" : "Insert Button"}
        open={showButtonModal}
        onOk={handleButtonModalOk}
        onCancel={handleButtonModalCancel}
        okText={editingButton ? "Update" : "Insert"}
        cancelText="Cancel"
        width={500}
        footer={[
          editingButton && (
            <Button key="remove" danger onClick={removeButton} style={{ float: "left" }}>
              Remove Button
            </Button>
          ),
          <Button key="cancel" onClick={handleButtonModalCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleButtonModalOk}>
            {editingButton ? "Update" : "Insert"}
          </Button>,
        ]}
      >
        <Form
          form={buttonForm}
          layout="vertical"
          initialValues={{
            buttonText: "",
            buttonLink: "",
            buttonAlignment: "left",
          }}
        >
          <Form.Item
            label="Button Text"
            name="buttonText"
            rules={[
              { required: true, message: "Please enter button text" },
            ]}
          >
            <Input placeholder="Enter text for the button" />
          </Form.Item>

          <Form.Item
            label="Button Link"
            name="buttonLink"
            rules={[
              { required: true, message: "Please enter button link" },
              { type: "url", message: "Please enter a valid URL" },
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          <Form.Item
            label="Button Alignment"
            name="buttonAlignment"
          >
            <Radio.Group
              options={[
                { value: "left", label: "Left" },
                { value: "center", label: "Center" },
                { value: "right", label: "Right" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RichTextEditor;

